/* global HTMLDivElement, KeyboardEvent */
/**
 * InboxClarifyModal — pre-fill & chỉnh trước khi convert inbox item.
 * D18: Không convert nhanh; user chọn type + sửa field → xác nhận.
 * Hỗ trợ 5 đích: task / project / goal / note / habit (D19 P1).
 * A7: queue mode — khi onNext được truyền vào, sau convert/skip tự động sang mục tiếp theo.
 */
import { useEffect, useRef, useState } from "react";
import { X, SkipForward } from "lucide-react";
import { todayIso } from "../lib/date";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { InboxConversionType, InboxItem } from "../types";
import { useFocusTrap } from "../lib/focus-trap";
import { aiAvailable } from "../lib/ai/ai-config";
import { AiCaptureButton } from "./ai-capture-button";
import {
  TYPES,
  guessType,
  buildTarget,
  LabelInput,
  TaskFields,
  EventFields,
  ProjectFields,
  GoalFields,
  HabitFields,
  NoteFields,
  type ClarifyPrefill,
  type ClarifyQueueContext,
} from "./inbox-clarify-steps";

// Re-export types so existing importers (e.g. inbox-view.tsx) keep resolving.
export type { ClarifyPrefill, ClarifyQueueContext } from "./inbox-clarify-steps";

interface Props {
  item: InboxItem;
  api: LifeOsApi;
  onClose: () => void;
  /** When provided, pre-populates form fields from NL parser result. */
  prefill?: ClarifyPrefill;
  /** A7: when present, enables queue mode (progress indicator + Skip button). */
  queue?: ClarifyQueueContext;
}

// ── Main modal ───────────────────────────────────────────────────────────────
export function InboxClarifyModal({ item, api, onClose, prefill, queue }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);

  // Escape closes the modal (calls onClose, which handles queue too via caller)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // NL parser caught a clock time ("3pm", "15h") → almost certainly an event;
  // defaulting to Lịch keeps the parsed time instead of silently dropping it.
  const [type, setType] = useState<InboxConversionType>(prefill?.time ? "event" : guessType(item));
  const [fields, setFields] = useState<Record<string, string>>({
    // prefill.title overrides item.text when NL parser extracted a clean title
    title: prefill?.title ?? item.text,
    dueDate: prefill?.dueDate ?? todayIso(),
    time: prefill?.time ?? "09:00",
    priority: prefill?.priority ?? "medium",
    estimateMinutes: "30",
    area: "Chung",
    deadline: prefill?.dueDate ?? todayIso(),
    nextAction: "",
    horizon: "quarter",
    metric: "",
    target: "100",
    body: item.text,
    // tags: join prefill array or empty; NoteFields shows comma-separated
    tags: prefill?.tags?.join(", ") ?? "",
    // D19 habit defaults
    targetPerWeek: "5",
    habitColor: "#7c3aed",
    // P3-4: event recurrence (none = single occurrence)
    recurrence: "none",
  });

  const set = (k: string, v: string) => setFields((prev) => ({ ...prev, [k]: v }));

  // AI capture result: merge prefill fields + update type. User still confirms.
  const handleAiResult = (p: ClarifyPrefill, t: InboxConversionType) => {
    setType(t);
    setFields((prev) => ({
      ...prev,
      ...(p.title    != null ? { title: p.title }                          : {}),
      ...(p.dueDate  != null ? { dueDate: p.dueDate, deadline: p.dueDate } : {}),
      ...(p.time     != null ? { time: p.time }                            : {}),
      ...(p.priority != null ? { priority: p.priority }                    : {}),
      ...(p.tags     != null ? { tags: p.tags.join(", ") }                 : {}),
      ...(p.estimateMinutes != null ? { estimateMinutes: String(p.estimateMinutes) } : {}),
      // Goal-specific (chỉ có giá trị khi type=goal; field khác bỏ qua vô hại)
      ...(p.horizon  != null ? { horizon: p.horizon }                      : {}),
      ...(p.metric   != null ? { metric: p.metric }                        : {}),
      ...(p.target   != null ? { target: String(p.target) }                : {}),
    }));
  };

  // After convert: advance queue or close (single-item mode).
  const handleSubmit = () => {
    const target = buildTarget(type, fields);
    if (!target) return; // title empty — blocked
    api.actions.convertInboxItem(item.id, target);
    if (queue) queue.onNext();
    else onClose();
  };

  // A7 Skip: do NOT convert; just advance to next item.
  const handleSkip = () => {
    if (queue) queue.onNext();
    else onClose();
  };

  const typeMeta = TYPES.find((t) => t.value === type)!;
  const titleLabel =
    type === "project" ? "Tên dự án" :
    type === "habit"   ? "Tên thói quen" :
    "Tiêu đề";

  // A7: is this the last item in the queue?
  const isLast = queue ? queue.index === queue.total : false;

  return (
    /* backdrop */
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="inbox-clarify-title">
      <div ref={panelRef} className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* header — progress pill in queue mode */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 id="inbox-clarify-title" className="font-display text-[17px] font-bold">Phân loại inbox</h2>
            {queue && (
              <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[11px] font-bold text-brand-700">
                Mục {queue.index}/{queue.total}
              </span>
            )}
          </div>
          <button onClick={onClose} className="grid h-7 w-7 place-items-center rounded-lg text-ink-500 hover:bg-ink-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* source text (read-only reference) */}
        <p className="mb-2 rounded-2xl bg-ink-50 px-3 py-2 text-[12px] text-ink-600 line-clamp-2">{item.text}</p>

        {/* AI analyse — tự chạy ngay khi mở popup (autoRun); nút còn lại để phân tích lại.
            Chỉ khi bật AI + đăng nhập + online. Modal remount theo từng item nên fire 1 lần/mục. */}
        {aiAvailable(api) && (
          <div className="mb-4">
            <AiCaptureButton text={item.text} onResult={handleAiResult} autoRun />
          </div>
        )}

        {/* type tabs — 6 đích, chia 2 hàng × 3 cho khỏi chật trong max-w-md */}
        <div className="mb-4 grid grid-cols-3 gap-1.5">
          {TYPES.map((t) => (
            <button key={t.value} onClick={() => setType(t.value)}
              className={`rounded-xl py-1.5 text-[12px] font-bold transition ${type === t.value ? "gbrand text-white" : "bg-ink-100 text-ink-600 hover:bg-ink-200"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* fields */}
        <div className="flex flex-col gap-3">
          <LabelInput label={titleLabel} id="title" value={fields.title} onChange={(v) => set("title", v)} placeholder="Tiêu đề…" />
          {type === "task"    && <TaskFields    f={fields} set={set} />}
          {type === "event"   && <EventFields   f={fields} set={set} />}
          {type === "project" && <ProjectFields f={fields} set={set} />}
          {type === "goal"    && <GoalFields    f={fields} set={set} />}
          {type === "note"    && <NoteFields    f={fields} set={set} />}
          {type === "habit"   && <HabitFields   f={fields} set={set} />}
        </div>

        {/* actions — queue mode adds Skip; last item shows "Xong" instead of next */}
        <div className="mt-5 flex gap-2">
          {queue ? (
            // Queue mode: [Bỏ qua] [Tạo X →] or [Tạo X · Xong] on last
            <>
              <button
                onClick={handleSkip}
                title="Bỏ qua mục này, sang mục tiếp theo"
                className="flex items-center gap-1 rounded-2xl bg-ink-100 px-3 py-2.5 text-[13px] font-bold text-ink-600 hover:bg-ink-200"
              >
                <SkipForward className="h-3.5 w-3.5" />
                Bỏ qua
              </button>
              <button
                onClick={handleSubmit}
                disabled={!fields.title.trim()}
                className="flex-1 rounded-2xl gbrand py-2.5 text-[13px] font-bold text-white disabled:opacity-50"
              >
                {isLast ? `Tạo ${typeMeta.label} · Xong` : `Tạo ${typeMeta.label} →`}
              </button>
            </>
          ) : (
            // Single-item mode (unchanged)
            <>
              <button onClick={onClose} className="flex-1 rounded-2xl bg-ink-100 py-2.5 text-[13px] font-bold text-ink-600 hover:bg-ink-200">Huỷ</button>
              <button onClick={handleSubmit} disabled={!fields.title.trim()}
                className="flex-1 rounded-2xl gbrand py-2.5 text-[13px] font-bold text-white disabled:opacity-50">
                Tạo {typeMeta.label}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
